// InvestmentChartRechartsAntD.jsx
import React, { useMemo, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import {
  Row,
  Col,
  Card,
  InputNumber,
  Slider,
  Statistic,
  Segmented,
  Space,
} from 'antd';

// helper: currency formatter for labels/statistics
const fmtCurrency = (n) =>
  n.toLocaleString(undefined, { maximumFractionDigits: 0 });

const Chart = () => {
  const [monthly, setMonthly] = useState(500); // $/mo
  const [annualReturn, setAnnualReturn] = useState(7); // % per year
  const [years, setYears] = useState(30); // years
  const [timing, setTiming] = useState('End of month'); // or "Start of month"

  const data = useMemo(() => {
    const months = Math.max(1, years) * 12;
    const rMonthly = Math.pow(1 + annualReturn / 100, 1 / 12) - 1;

    let balance = 0;
    const points = [{ year: 0, balance: 0 }];

    for (let m = 1; m <= months; m++) {
      if (timing === 'Start of month') {
        balance += monthly; // contribute first
        balance *= 1 + rMonthly; // then grow
      } else {
        balance *= 1 + rMonthly; // grow first
        balance += monthly; // then contribute
      }

      if (m % 12 === 0) {
        points.push({ year: m / 12, balance: Math.round(balance) });
      }
    }
    return points;
  }, [monthly, annualReturn, years, timing]);

  const finalBalance = data[data.length - 1]?.balance ?? 0;

  return (
    <Card
      style={{ maxWidth: 1000, margin: '0 auto' }}
      bodyStyle={{ padding: 16 }}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12} lg={6}>
            <label style={{ display: 'block', marginBottom: 6 }}>
              Contribución mensual
            </label>
            <InputNumber
              value={monthly}
              min={0}
              step={50}
              addonBefore="$"
              style={{ width: '100%' }}
              formatter={(v) => (v === '' ? '' : `$ ${fmtCurrency(Number(v))}`)}
              parser={(v) => Number(String(v).replace(/[^\d.-]/g, '')) || 0}
              onChange={(val) => setMonthly(Number(val || 0))}
            />
            <Slider
              min={0}
              max={5000}
              step={50}
              value={Math.min(monthly, 5000)}
              onChange={setMonthly}
              style={{ marginTop: 10 }}
            />
          </Col>

          <Col xs={24} md={12} lg={6}>
            <label style={{ display: 'block', marginBottom: 6 }}>
              Retorno anual esperado
            </label>
            <InputNumber
              value={annualReturn}
              min={0}
              max={20}
              step={0.1}
              addonAfter="%"
              style={{ width: '100%' }}
              formatter={(v) => (v === '' ? '' : `${v}%`)}
              parser={(v) => Number(String(v).replace(/[^\d.-]/g, '')) || 0}
              onChange={(val) => setAnnualReturn(Number(val || 0))}
            />
            <Slider
              min={0}
              max={20}
              step={0.1}
              value={Math.min(annualReturn, 20)}
              onChange={setAnnualReturn}
              style={{ marginTop: 10 }}
            />
          </Col>

          <Col xs={24} md={12} lg={6}>
            <label style={{ display: 'block', marginBottom: 6 }}>
              Años de inversión
            </label>
            <InputNumber
              value={years}
              min={1}
              max={60}
              step={1}
              style={{ width: '100%' }}
              onChange={(val) => setYears(Number(val || 1))}
            />
            <Slider
              min={1}
              max={60}
              step={1}
              value={Math.min(years, 60)}
              onChange={setYears}
              style={{ marginTop: 10 }}
            />
          </Col>

          <Col xs={24} md={12} lg={6}>
            {/* <label style={{ display: 'block', marginBottom: 6 }}>
              Momento de la contribución
            </label>
            <Segmented
              block
              value={timing}
              onChange={setTiming}
              options={['Fin de mes', 'Inicio de mes']}
            /> */}
            <Statistic
              style={{ marginTop: 12 }}
              title={`Balance final (fin del año ${years})`}
              value={`$${fmtCurrency(finalBalance)}`}
            />
          </Col>
        </Row>

        <div style={{ width: '100%', height: 380 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={data}
              margin={{ top: 10, right: 20, bottom: 10, left: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="year"
                tickFormatter={(y) => `${y}`}
                label={{ value: 'Años', position: 'insideBottom', offset: -5 }}
              />
              <YAxis
                tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                label={{
                  // value: 'Balance ($)',
                  angle: -90,
                  position: 'insideLeft',
                }}
              />
              <Tooltip
                formatter={(v) => [`$${fmtCurrency(Number(v))}`, 'Balance']}
                labelFormatter={(l) => `Año ${l}`}
              />
              <Line
                type="monotone"
                dataKey="balance"
                dot={false}
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Space>
    </Card>
  );
};

export default Chart;
